import type {
  CellContext,
  Column,
  ColumnDef,
  ColumnMeta,
  PaginationState,
  RowData,
  SortingState,
  Table as TanStackTable,
} from "@tanstack/solid-table";
import {
  columnFilteringFeature,
  columnOrderingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createTable,
  flexRender,
  FlexRender,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
} from "@tanstack/solid-table";
import type { PaginatedRequestOptions, PaginatedResponse } from "../../../services/ApiService";
import { SortDirection } from "../../../services/ApiService";
import type { UseQueryResult } from "@tanstack/solid-query";
import type { TranslationKey } from "../../../state/i18n";
import { useTranslation } from "../../../state/i18n";
import type { Accessor, Component, JSX, Setter } from "solid-js";
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  Show,
  Suspense,
  Switch,
  untrack,
  useContext,
} from "solid-js";
import { cloneDeep, isEmpty, isEqual, isNil } from "lodash-es";
import { condition } from "../../../utils/solidUtils";
import Pagination from "../../../components/pagination/Pagination";
import Popover from "../../../components/popover/Popover";
import { IconButton, TextAndIconButton, TextButton } from "../../../components/button/Button";
import DraggableList from "../dnd/DraggableList";
import Draggable from "../dnd/Draggable";
import Handlebar from "../../../components/field/Handlebar";
import Text from "../../../components/text/Text";
import DropIndicator from "../dnd/DropIndicator";
import { Checkbox, type CheckedState } from "../../../components/checkbox/Checkbox";
import { Icon, type IconName } from "../../../components/icon/Icon";
import type { PartialBy, Stringifiable } from "../../../types";
import type { Filter } from "./filter";
import { toApi } from "./filter";
import { Key } from "@solid-primitives/keyed";
import uuid from "../../../utils/uuid";
import { noop } from "../../../utils/noop";
import TableActiveFilter from "./TableActiveFilter";
import { CompactSingleSelect } from "../../../components/menu/Select";
import { useTable } from "./TableProvider";
import type { ViewConfigurationStoreState } from "../../../utils/viewConfigurationStore";
import { createViewConfigurationStore } from "../../../utils/viewConfigurationStore";
import { createWritableMemo } from "@solid-primitives/memo";
import { DynamicValueIconTile } from "../../../components/icon/IconTile";

const selectColumnId = "select-column";
const actionColumnId = "action-column";

const nonDataColumns = [selectColumnId, actionColumnId];

const tableFeaturesConfig = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  columnVisibilityFeature,
  columnOrderingFeature,
  columnSizingFeature,
  columnFilteringFeature,
});

export type EnabledTableFeatures = typeof tableFeaturesConfig;

const asColumnDefWithId = <T extends RowData>(columnDef: ColumnDef<EnabledTableFeatures, T>): ColumnDefWithId<T> => {
  if (!isNil(columnDef.id)) {
    return columnDef as ColumnDefWithId<T>;
  } else {
    throw new Error("All column definitions are required to have an 'id'");
  }
};

export type ColumnDefWithId<T extends RowData> = ColumnDef<EnabledTableFeatures, T> & {
  id: string;
  meta: ColumnMeta<EnabledTableFeatures, T, unknown>;
};

export type TableProps<T extends RowData> = {
  columns: ColumnDefWithId<T>[];
  defaultVisibleColumns: string[];
  defaultSorting?: SortingState;
  fetchData: (options: PaginatedRequestOptions) => UseQueryResult<PaginatedResponse<T>>;
  actionButton?: (context: CellContext<EnabledTableFeatures, T, unknown>) => JSX.Element;
  emptyState?: JSX.Element;
  children: JSX.Element;
  serializeConfigurationStore?: (value: ViewConfigurationStoreState) => string;
  deserializeConfigurationStore?: (value: string) => ViewConfigurationStoreState;
  persistKey?: string;
} & TableBulkSelect;

type TableBulkSelect = TableBulkSelectEnabled | TableBulkSelectDisabled;

type TableBulkSelectEnabled = {
  bulkSelect: true;
  bulkSelectActions: {
    icon: IconName;
    text: string;
    onClick: (selectedIds: string[]) => void;
  }[];
};

type TableBulkSelectDisabled = {
  bulkSelect: false;
};

type CreatingFilterState = {
  filter: PartialBy<Filter, "value">;
  columnDef: ColumnDefWithId<RowData>;
};

type TableInstanceContextValue = {
  table: TanStackTable<EnabledTableFeatures, RowData>;
  columnOrder: Accessor<string[]>;
  displayData: Accessor<PaginatedResponse<RowData> | undefined>;
  dataQuery: Accessor<UseQueryResult<PaginatedResponse<RowData>>>;
  pagination: Accessor<PaginationState>;
  selectedRowIds: Accessor<string[]>;
  clearRowSelection: () => void;
  bulkSelectActions: { icon: IconName; text: string; onClick: (ids: string[]) => void }[];
  creatingFilter: Accessor<CreatingFilterState | null>;
  setCreatingFilter: Setter<CreatingFilterState | null>;
  editingFilter: Accessor<string | null | undefined>;
  setEditingFilter: Setter<string | null | undefined>;
};

const TableInstanceContext = createContext<TableInstanceContextValue>();

const useTableInstance = (): TableInstanceContextValue => {
  const ctx = useContext(TableInstanceContext);
  if (isNil(ctx)) {
    throw new Error("useTableInstance must be used within a Table component");
  }
  return ctx;
};

const Table = <T extends { id: Stringifiable }>(props: TableProps<T>) => {
  const { t } = useTranslation();
  const tableState = useTable();

  const selectedRowIds = createMemo(() => {
    if (props.bulkSelect) {
      return Object.entries(tableState.rowSelection())
        .filter(([, value]) => value)
        .map(([key]) => key);
    }
    return [];
  });
  const defaultVisibility = createMemo(() => {
    const initialObject: Record<string, boolean> = {};
    initialObject[selectColumnId] = props.bulkSelect;
    initialObject[actionColumnId] = !isNil(props.actionButton);

    return props.columns.reduce<Record<string, boolean>>((acc, column) => {
      acc[column.id] = props.defaultVisibleColumns.includes(column.id);
      return acc;
    }, initialObject);
  });

  const columns = createMemo(
    () =>
      [
        ...(props.bulkSelect
          ? [
              {
                id: selectColumnId,
                accessorKey: selectColumnId,
                meta: {
                  name: "",
                  icon: {
                    name: "user",
                    colors: "bg-surface-subtle icon-secondary",
                  },
                  filter: {
                    type: "none",
                    definition: [],
                  },
                },
                maxSize: 40,
                size: 40,
                enableHiding: false,
                header: (context) => {
                  const selected = createMemo<CheckedState>(() => {
                    const pageIds = Object.keys(context.table.getRowModel().rowsById);
                    if (isEmpty(pageIds)) {
                      return false;
                    }

                    const selectedRows = tableState.rowSelection();
                    const selectedIds = Object.keys(selectedRows);
                    const selectedIdsOnCurrentPage = selectedIds.filter((id) =>
                      Boolean(selectedRows[id] && pageIds.includes(id)),
                    );

                    if (selectedIdsOnCurrentPage.length === pageIds.length) {
                      return true;
                    } else if (isEmpty(selectedIdsOnCurrentPage)) {
                      return false;
                    } else {
                      return "indeterminate";
                    }
                  });

                  return (
                    <Checkbox
                      checked={selected()}
                      ariaLabel={t(($) => $.TABLE_HEADER_BULK_SELECT_SELECT_ALL_ARIA)}
                      setChecked={(futureValue) => {
                        tableState.setRowSelection((prev) => {
                          const next = { ...prev };
                          Object.keys(context.table.getRowModel().rowsById).forEach((id) => {
                            if (futureValue === true) {
                              next[id] = true;
                            } else {
                              delete next[id];
                            }
                          });
                          return next;
                        });
                      }}
                    />
                  );
                },
                cell: (context) => (
                  <Checkbox
                    checked={context.row.getIsSelected()}
                    setChecked={() => context.row.toggleSelected()}
                    ariaLabel={t(($) => $.TABLE_ROW_SELECT_ARIA)}
                  />
                ),
              } as ColumnDefWithId<T>,
            ]
          : []),
        ...(!isNil(props.actionButton)
          ? [
              {
                id: actionColumnId,
                meta: {
                  name: t(($) => $.TABLE_COLUMNS_ACTIONS_HEADER),
                  icon: {
                    name: "user",
                    colors: "bg-surface-subtle icon-secondary",
                  },
                  filter: {
                    type: "none",
                    definition: [],
                  },
                },
                accessorKey: actionColumnId,
                header: () => <span>{t(($) => $.TABLE_COLUMNS_ACTIONS_HEADER)}</span>,
                minSize: 140,
                size: 140,
                enableHiding: false,
                cell: props.actionButton,
              } as ColumnDefWithId<T>,
            ]
          : []),
        ...props.columns,
      ] as ColumnDefWithId<T>[],
  );

  const { sorting, setSorting, columnOrder, setColumnOrder, columnVisibility, setColumnVisibility, filterStore } =
    createViewConfigurationStore(
      props.defaultSorting ?? [],
      columns().map((column) => column.id),
      cloneDeep(defaultVisibility()),
      [],
      props.persistKey,
      props.serializeConfigurationStore,
      props.deserializeConfigurationStore,
    );

  // Compared by value so that a filter edit which leaves the query identical does not refetch.
  const filterQuery = createMemo(() => filterStore.all().map(toApi), undefined, { equals: isEqual });

  untrack(() => {
    tableState.setFilterStore(filterStore);
  });

  const [pagination, setPagination] = createWritableMemo<PaginationState>((previous) => {
    // Reset pagination when filters change
    filterQuery();
    return previous?.pageIndex === 0 ? previous : { pageIndex: 0, pageSize: previous?.pageSize ?? 20 };
  });

  const dataQuery = createMemo(() =>
    props.fetchData({
      pagination: {
        page: pagination().pageIndex,
        size: pagination().pageSize,
      },
      query: filterQuery(),
      sort: sorting().map(({ id, desc }) => ({ key: id, direction: desc ? SortDirection.Desc : SortDirection.Asc })),
    }),
  );

  // We need the previous data for displaying until new data has been fetched, otherwise the complete UI will be updated
  // when fetching, since 'data' is empty during that time
  const [displayData, setDisplayData] = createSignal<PaginatedResponse<T>>();
  createEffect(() => {
    if (!dataQuery().isFetching && !isNil(dataQuery().data)) {
      setDisplayData(dataQuery().data);
      tableState.setTotalCount(dataQuery().data!.totalSize);
    }
  });

  const bulkSelectActions = createMemo(() => {
    if (props.bulkSelect) {
      return props.bulkSelectActions;
    }
    return [];
  });

  createEffect(() => {
    const currentColumns = columns();
    const currentVisibility = columnVisibility();

    const enabledIds = Object.keys(currentVisibility).filter((columnId) => currentVisibility[columnId]);

    const columnIds = enabledIds.reduce<string[]>((acc, columnId) => {
      const id = currentColumns.find((column) => column.id === columnId)?.id;
      if (!isNil(id) && !isEmpty(id) && !nonDataColumns.includes(id)) {
        acc.push(id);
      }

      return acc;
    }, []);

    tableState.setColumnVisibility(columnIds);
  });

  const table = createTable({
    features: tableFeaturesConfig,
    get data() {
      return displayData()?.elements ?? [];
    },
    get columns() {
      return columns();
    },
    onRowSelectionChange: untrack(() => (props.bulkSelect ? tableState.setRowSelection : undefined)),
    enableRowSelection: untrack(() => props.bulkSelect),
    defaultColumn: {
      header: (context) => <HeaderCellContent columnDef={context.column.columnDef} />,
      minSize: 200,
      maxSize: 352,
      sortDescFirst: true,
      enableSorting: false,
      enableColumnFilter: false,
    },
    getRowId: (row) => row.id.toString(),
    manualPagination: true,
    manualSorting: true,
    state: {
      get pagination() {
        return pagination();
      },
      get sorting() {
        return sorting();
      },
      get columnOrder() {
        return columnOrder();
      },
      get columnVisibility() {
        return columnVisibility();
      },
      get rowSelection() {
        return props.bulkSelect ? tableState.rowSelection() : {};
      },
    },
    initialState: {
      columnOrder: untrack(() => columnOrder()),
      columnVisibility: untrack(() => defaultVisibility()),
      rowSelection: untrack(() => (props.bulkSelect ? tableState.rowSelection() : undefined)),
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const clearRowSelection = () => {
    if (props.bulkSelect) {
      tableState.setRowSelection({});
    }
  };

  const [editingFilter, setEditingFilter] = createSignal<string | null>();
  const [creatingFilter, setCreatingFilter] = createSignal<{
    filter: PartialBy<Filter, "value">;
    columnDef: ColumnDefWithId<T>;
  } | null>();

  const handleFilterCreate = (filter: PartialBy<Filter, "value">, columnDef: ColumnDefWithId<T>) => {
    setCreatingFilter({ filter, columnDef });
  };

  // Compared by reference because this re-runs on every table state change, while the result rarely changes
  const filterableColumns = createMemo(
    () => props.columns.filter((columnDef) => table.getColumn(columnDef.id)?.getCanFilter() ?? false),
    undefined,
    {
      equals: (prev, next) =>
        prev.length === next.length && prev.every((columnDef, index) => columnDef === next[index]),
    },
  );

  // Push filterable columns into context so external components (e.g. TableFilterButton in a parent) can read them
  createEffect(() => {
    tableState.setFilterableColumns(filterableColumns() as ColumnDefWithId<RowData>[]);
  });

  // Register the create-filter handler in context once so external callers can trigger it
  untrack(() => {
    tableState.setCreateFilterHandler((columnId: string) => {
      const columnDef = untrack(() => filterableColumns()).find((col) => col.id === columnId);
      if (!isNil(columnDef)) {
        handleFilterCreate(
          {
            id: uuid(),
            type: columnDef.meta.filter.type,
            path: columnDef.id,
            operator: columnDef.meta.filter.definition[0].operator,
          },
          columnDef,
        );
      }
    });
  });

  const showEmptyState = createMemo(
    () => !isNil(displayData()) && (displayData()?.totalSize ?? 0) === 0 && isEmpty(filterStore.all()),
  );

  const contextValue: TableInstanceContextValue = {
    table: table as TanStackTable<EnabledTableFeatures, RowData>,
    columnOrder,
    displayData: displayData as Accessor<PaginatedResponse<RowData> | undefined>,
    dataQuery: dataQuery as Accessor<UseQueryResult<PaginatedResponse<RowData>>>,
    pagination,
    selectedRowIds,
    clearRowSelection,
    bulkSelectActions: untrack(() => bulkSelectActions()),
    creatingFilter: creatingFilter as Accessor<CreatingFilterState | null>,
    setCreatingFilter: setCreatingFilter as Setter<CreatingFilterState | null>,
    editingFilter,
    setEditingFilter,
  };

  return (
    <TableInstanceContext.Provider value={contextValue}>
      <Suspense>
        <Show when={showEmptyState()} fallback={props.children}>
          {props.emptyState}
        </Show>
      </Suspense>
    </TableInstanceContext.Provider>
  );
};

export const TableColumnSettings = () => {
  const { table, columnOrder } = useTableInstance();
  return (
    <ColumnSettingsButton
      table={table as unknown as TanStackTable<EnabledTableFeatures, never>}
      columnOrder={columnOrder()}
    />
  );
};

export const TableActiveFilters = () => {
  const { t } = useTranslation();
  const { filterStore } = useTable();
  const { table, creatingFilter, setCreatingFilter, editingFilter, setEditingFilter } = useTableInstance();
  const allFilters = createMemo<Filter[]>(() => filterStore()?.all() ?? []);

  return (
    <Show when={!isEmpty(allFilters()) || !isNil(creatingFilter())}>
      <div class="mt-4 flex flex-wrap items-center gap-2">
        <div class="text-base-regular text-secondary">{t(($) => $.TABLE_FILTER_ACTIVE_FILTERS)}</div>
        <Key each={allFilters()} by="id">
          {(filter) => {
            const column = table.getColumn(filter().path);
            if (isNil(column)) {
              throw new Error(`Could not find column with id '${filter().path}'`);
            }
            return (
              <TableActiveFilter
                filter={filter()}
                columnDef={asColumnDefWithId(column.columnDef)}
                onSave={(filter) => {
                  filterStore()?.upsert(filter);
                }}
                onClose={() => {
                  setEditingFilter();
                }}
                onRemove={() => filterStore()?.remove(filter().id)}
                open={filter().id === editingFilter()}
              />
            );
          }}
        </Key>
        <Show when={creatingFilter()}>
          {(filter) => (
            <TableActiveFilter
              filter={filter().filter}
              columnDef={filter().columnDef}
              onSave={(filter) => {
                filterStore()?.add(filter);
                setCreatingFilter(null);
                setEditingFilter();
              }}
              onClose={() => setCreatingFilter(null)}
              onRemove={noop}
              open
            />
          )}
        </Show>
      </div>
    </Show>
  );
};

type TableBodyProps = {
  emptyFilteredText?: TranslationKey;
  loadingText?: TranslationKey;
};

export const TableBody = (props: TableBodyProps) => {
  const { t } = useTranslation();
  const { table, displayData, dataQuery, selectedRowIds, setCreatingFilter } = useTableInstance();

  return (
    <div
      tabIndex={-1}
      class="relative flex shrink overflow-x-auto overflow-y-hidden rounded-t border border-subtle"
      classList={{ "rounded-b": isEmpty(selectedRowIds()) }}
    >
      <Show when={dataQuery().isFetching}>
        <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
          <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
          <Show when={props.loadingText}>
            {(loadingText) => <span class="text-medium-regular text-primary">{t(loadingText())}</span>}
          </Show>
        </div>
      </Show>
      <table class="-mb-px grid w-full shrink divide-y divide-subtle border-b border-subtle text-left">
        <thead class="bg-surface-extra-subtle">
          <For each={table.getHeaderGroups()}>
            {(headerGroup) => (
              <tr class="flex divide-x divide-subtle">
                <For each={headerGroup.headers}>
                  {(header) => {
                    const columnDef = asColumnDefWithId(header.column.columnDef);
                    const columnName = columnDef.meta.name;

                    return (
                      <th
                        class="bg-surface-extra-subtle p-2 pl-3 text-base-medium text-primary"
                        style={{ width: `${header.getSize()}px` }}
                        aria-label={columnName}
                      >
                        <div class="flex h-6 items-center gap-1.5">
                          <FlexRender header={header} />

                          <div class="ml-auto flex gap-1">
                            <Show when={header.column.getCanSort()}>
                              <SortButton column={header.column} />
                            </Show>
                            <Show when={header.column.getCanFilter()}>
                              <IconButton
                                icon="filter"
                                size="small"
                                variant="tertiary"
                                aria-label={t(($) => $.TABLE_COLUMN_ADD_FILTER, { columnName })}
                                onClick={() => {
                                  setCreatingFilter({
                                    filter: {
                                      id: uuid(),
                                      type: columnDef.meta.filter.type,
                                      path: columnDef.id,
                                      operator: columnDef.meta.filter.definition[0].operator,
                                    },
                                    columnDef,
                                  });
                                }}
                              />
                            </Show>
                          </div>
                        </div>
                      </th>
                    );
                  }}
                </For>
              </tr>
            )}
          </For>
        </thead>
        <tbody
          tabIndex={0} // oxlint-disable-line jsx-a11y/no-noninteractive-tabindex
          class="divide-y divide-subtle overflow-y-auto -outline-offset-1 focus-visible:outline-default"
          classList={{
            // Create some room for initial loading message
            "min-h-20": isEmpty(displayData()?.elements) && dataQuery().isFetching,
          }}
        >
          <For each={table.getRowModel().rows}>
            {(row) => (
              <tr class="flex divide-x divide-subtle">
                <For each={row.getVisibleCells()}>
                  {(cell) => (
                    <td class="p-0 align-top wrap-anywhere" style={{ width: `${cell.column.getSize()}px` }}>
                      <div class="max-h-74 min-h-9 overflow-y-auto px-3 py-2 text-base-regular text-secondary">
                        {/* In dev builds the first getContext() reads table.options (debug flags); FlexRender would track
                        that and remount every component cell on the next state change, dropping focus */}
                        {untrack(() => flexRender(cell.column.columnDef.cell, cell.getContext()))}
                      </div>
                    </td>
                  )}
                </For>
              </tr>
            )}
          </For>

          <Show
            when={condition(props.emptyFilteredText, () => !isNil(displayData()) && isEmpty(displayData()?.elements))}
          >
            {(emptyFilteredText) => (
              <div class="mt-4 ml-4 h-60 text-medium-regular text-secondary">{t(emptyFilteredText())}</div>
            )}
          </Show>
        </tbody>
      </table>
    </div>
  );
};

export const TableBulkSelectBar = () => {
  const { t } = useTranslation();
  const { selectedRowIds, bulkSelectActions, displayData, clearRowSelection } = useTableInstance();

  return (
    <Show when={!isEmpty(selectedRowIds())}>
      <div class="flex items-center justify-between rounded-b border-x border-b border-default bg-surface-extra-subtle py-2 pr-1.5 pl-4">
        <span class="text-base-medium text-primary">
          {selectedRowIds().length} <span class="text-base-regular">{t(($) => $.TABLE_SELECTION_OF)}</span>{" "}
          {displayData()?.totalSize ?? 0} <span class="text-base-regular">{t(($) => $.TABLE_SELECTION_SELECTED)}</span>
        </span>
        <div class="flex">
          <div class="flex gap-1">
            <For each={bulkSelectActions}>
              {(item) => (
                <TextAndIconButton
                  icon={item.icon}
                  text={item.text}
                  onClick={() => item.onClick(selectedRowIds())}
                  variant="secondary"
                />
              )}
            </For>
          </div>
          <span class="mr-1 ml-2 border-l border-default" />
          <IconButton icon="x" variant="tertiary" aria-label={t(($) => $.CANCEL_BUTTON)} onClick={clearRowSelection} />
        </div>
      </div>
    </Show>
  );
};

type TablePaginationProps = {
  pageSizeLabel: TranslationKey;
};

export const TablePagination = (props: TablePaginationProps) => {
  const { table, pagination, displayData } = useTableInstance();

  return (
    <div class="mt-3 flex h-7.5 shrink-0">
      <Show when={condition(displayData()?.totalSize ?? 0, (totalSize) => totalSize > pagination().pageSize)}>
        {(totalSize) => (
          <Pagination
            initialPage={pagination().pageIndex + 1}
            totalElements={totalSize()}
            pageSize={pagination().pageSize}
            onPageChange={(page) => {
              table.setPageIndex(page - 1);
            }}
          />
        )}
      </Show>

      <div class="ml-auto">
        <PageSize
          pageSize={pagination().pageSize}
          onPageSizeChange={(value) => table.setPageSize(value)}
          pageSizeLabel={props.pageSizeLabel}
        />
      </div>
    </div>
  );
};

type ColumnSettingsButtonProps<T extends RowData> = {
  table: TanStackTable<EnabledTableFeatures, T>;
  columnOrder: string[];
};
const ColumnSettingsButton = <T extends RowData>(props: ColumnSettingsButtonProps<T>) => {
  const { t } = useTranslation();
  let scrollContainerRef!: HTMLDivElement;

  return (
    <Popover
      variant="lg"
      Title={() => <h3 class="text-header-lg text-primary">{t(($) => $.TABLE_COLUMN_SETTINGS_POPOVER_TITLE)}</h3>}
      aria-label={t(($) => $.TABLE_COLUMN_SETTINGS_POPOVER_TITLE)}
      Trigger={(triggerProps) => (
        <TextAndIconButton
          {...triggerProps}
          icon="table"
          text={t(($) => $.TABLE_COLUMN_SETTINGS_TRIGGER_LABEL)}
          variant="secondary"
        />
      )}
      positioning={{ placement: "bottom-end" }}
      modal={false}
    >
      <div class="mt-1 flex min-h-0 flex-col">
        <p class="pr-2 pb-4 pl-4 text-base-regular text-secondary">
          {t(($) => $.TABLE_COLUMN_SETTINGS_POPOVER_DESCRIPTION)}
        </p>
        <div class="flex shrink flex-col overflow-y-hidden border-y border-subtle px-2 pt-4">
          <div class="flex items-center justify-between pr-1 pb-1 pl-2">
            <h4 class="text-header-md text-secondary">{t(($) => $.TABLE_COLUMN_SETTINGS_POPOVER_COLUMNS)}</h4>
            <TextButton
              variant="tertiary"
              size="medium"
              text={
                props.table.getIsAllColumnsVisible()
                  ? t(($) => $.TABLE_COLUMN_SETTINGS_VISIBILITY_HIDE_ALL_LABEL)
                  : t(($) => $.TABLE_COLUMN_SETTINGS_VISIBILITY_SHOW_ALL_LABEL)
              }
              onClick={() => {
                props.table.toggleAllColumnsVisible(!props.table.getIsAllColumnsVisible());
              }}
            />
          </div>

          <div class="shrink overflow-y-auto py-px" ref={scrollContainerRef}>
            <DraggableList
              scrollContainer={scrollContainerRef}
              monitorDraggableType="COLUMN"
              onDrop={(sourceData, index) => {
                const newColumnOrder = [...props.columnOrder];
                newColumnOrder.splice(index, 0, newColumnOrder.splice(sourceData.index, 1)[0]);
                props.table.setColumnOrder(newColumnOrder);
              }}
              items={props.columnOrder.map((id) => ({
                id,
              }))}
            >
              <ul>
                <For each={props.columnOrder}>
                  {(columnId, index) => {
                    const column = props.table.getColumn(columnId);
                    if (isNil(column)) {
                      throw new Error(`Could not find column with id '${columnId}'`);
                    }

                    if (column.id === selectColumnId || column.id === actionColumnId) {
                      return;
                    }

                    return (
                      <li aria-label={column.columnDef.meta?.name}>
                        <Item column={column} index={index()} isLast={index() === props.columnOrder.length - 1} />
                      </li>
                    );
                  }}
                </For>
              </ul>
            </DraggableList>
          </div>
        </div>

        <div class="px-4 pt-4">
          <TextButton
            variant="secondary"
            size="medium"
            text={t(($) => $.TABLE_COLUMN_SETTINGS_RESET_TO_DEFAULT_LABEL)}
            onClick={() => {
              props.table.resetColumnVisibility();
              props.table.resetColumnOrder();
            }}
            class="ml-auto"
          />
        </div>
      </div>
    </Popover>
  );
};

type ItemProps<T extends RowData> = {
  column: Column<EnabledTableFeatures, T>;
  index: number;
  isLast: boolean;
};
const Item = <T extends RowData>(props: ItemProps<T>) => {
  let dragHandle!: HTMLDivElement;
  const columnDef = createMemo(() => asColumnDefWithId(props.column.columnDef));

  return (
    <Draggable
      data={{
        type: "COLUMN",
        id: props.column.id,
        index: props.index,
      }}
      isLast={props.isLast}
      dragHandle={dragHandle}
      preview={() => (
        <div class="flex w-88 items-center gap-2.5 border border-default bg-surface-default py-2.5 pr-2 pl-3 shadow-elevation-light-3">
          <Handlebar />
          <Text class="text-base-regular text-primary" text={columnDef().meta.name} truncate />
        </div>
      )}
    >
      {(state) => (
        <div
          class="flex items-center gap-2.5 rounded-sm bg-surface-default py-1.5 pr-2 pl-3 hover:bg-surface-hover"
          classList={{ "opacity-50": state().type === "is-dragging" }}
        >
          <div class="cursor-grab" ref={dragHandle}>
            <Handlebar expandDragArea />
          </div>

          <Show when={condition(columnDef().meta.icon, (icon) => !isNil(icon))}>
            {(icon) => (
              <DynamicValueIconTile
                icon={icon().name}
                iconClassList={{
                  [icon().colors]: true,
                }}
                size="sm"
              />
            )}
          </Show>
          <Text class="text-base-regular text-primary" text={columnDef().meta.name} truncate />

          <div class="ml-auto">
            <ToggleVisibilityButton
              visible={props.column.getIsVisible()}
              onClick={() => props.column.toggleVisibility()}
            />
          </div>

          <Show when={state().type === "is-dragging-over" && !isNil(state().closestEdge)}>
            <DropIndicator edge={state().closestEdge!} gap={0} />
          </Show>
        </div>
      )}
    </Draggable>
  );
};

const ToggleVisibilityButton: Component<{ visible: boolean; onClick: () => void }> = (props) => {
  const { t } = useTranslation();

  return (
    <IconButton
      variant="tertiary"
      icon={props.visible ? "eye" : "eyeOff"}
      iconClass={!props.visible ? "icon-button-icon-tertiary-soft" : undefined}
      size="medium"
      aria-label={
        props.visible
          ? t(($) => $.TABLE_COLUMN_SETTINGS_VISIBILITY_HIDE_SINGLE_ARIA_LABEL)
          : t(($) => $.TABLE_COLUMN_SETTINGS_VISIBILITY_SHOW_SINGLE_ARIA_LABEL)
      }
      onClick={() => props.onClick()}
    />
  );
};

type HeaderCellContentProps<T extends RowData> = {
  columnDef: ColumnDef<EnabledTableFeatures, T>;
};
const HeaderCellContent = <T extends RowData>(props: HeaderCellContentProps<T>) => {
  const columnDef = createMemo(() => asColumnDefWithId(props.columnDef));

  return (
    <>
      <Show when={condition(columnDef().meta.icon, (icon) => !isNil(icon))}>
        {(icon) => (
          <div
            class="inline-flex size-5 shrink-0 items-center justify-center rounded-sm"
            classList={{
              [icon().colors]: true,
            }}
          >
            <Icon name={icon().name} size="sm" type="solid" />
          </div>
        )}
      </Show>
      <Text text={columnDef().meta.name} truncate />
    </>
  );
};

type SortButtonProps<T extends RowData> = {
  column: Column<EnabledTableFeatures, T>;
};
const SortButton = <T extends RowData>(props: SortButtonProps<T>) => {
  const { t } = useTranslation();

  return (
    <Switch
      fallback={
        <IconButton
          icon="switchVertical"
          size="small"
          variant="tertiary"
          aria-label={t(($) => $.TABLE_COLUMN_SORT_RESET, {
            columnName: props.column.columnDef.meta?.name,
          })}
          onClick={props.column.getToggleSortingHandler()}
        />
      }
    >
      <Match when={props.column.getIsSorted() === "desc"}>
        <IconButton
          icon="arrowSmDown"
          size="small"
          variant="tertiary"
          aria-label={t(($) => $.TABLE_COLUMN_SORT_DESCENDING, {
            columnName: props.column.columnDef.meta?.name,
          })}
          onClick={props.column.getToggleSortingHandler()}
        />
      </Match>
      <Match when={props.column.getIsSorted() === "asc"}>
        <IconButton
          icon="arrowSmUp"
          size="small"
          variant="tertiary"
          aria-label={t(($) => $.TABLE_COLUMN_SORT_ASCENDING, {
            columnName: props.column.columnDef.meta?.name,
          })}
          onClick={props.column.getToggleSortingHandler()}
        />
      </Match>
    </Switch>
  );
};

type PageSizeProps = {
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  pageSizeLabel: TranslationKey;
};
const PageSize: Component<PageSizeProps> = (props) => {
  const { t } = useTranslation();

  return (
    <div class="flex items-center gap-x-2">
      <label class="text-small-medium whitespace-nowrap text-secondary">{t(props.pageSizeLabel)}</label>
      <CompactSingleSelect
        ariaLabel={t(props.pageSizeLabel)}
        value={props.pageSize}
        options={[20, 50, 100].map((size) => ({
          variant: "text",
          label: size.toString(),
          value: size,
        }))}
        onSelect={(value) => props.onPageSizeChange(Number(value))}
        sameWidth={false}
      />
    </div>
  );
};

export default Table;
